import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import useAppBarTitle from 'hooks/use-app-bar-title';
import MaterialTable from 'components/table';
import { routes } from 'types/routes';
import { Root } from './user-activity-log-list.styled';
import { useRouter } from 'hooks/use-router';
import { ERoutesPrivate, ERoutesType } from 'types/routes';
import {
    TUserActivityLog,
    TGetUserActivityLogsRequest,
    TGetUserActivityLogsResponse,
    UserActionType,
} from 'types/entities/user-activity-logs';
import useUserActivityLogsGet from '../../hooks/use-user-activity-log-get';
import userActivityLogColumns from './user-activity-log-list.columns';
import queryString from 'query-string';
import UserActivityLogListFilters from '../../components/user-activity-log-list-filters';
import TablePagination from 'components/table/table-pagination/table-pagination';
import { Col } from 'components/layout/layout';
import NoDataFound from 'components/table/no-data-found/no-data-found';

export interface TUserActivityLogListFilters
    extends TGetUserActivityLogsRequest {
    page: number;
    limit: number;
}

const pageTitle = 'User Activity Logs';

const UserActivityLogList = () => {
    useAppBarTitle(pageTitle);
    const { history, location } = useRouter();
    // TODO: extract the following to custom hook?
    const filtersFromUrlQuery = queryString.parse(
        location.search,
    ) as TUserActivityLogListFilters;
    const defaultFilters = {
        nexId: '',
        page: 0,
        limit: 25,
        startDate: dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
        endDate: dayjs().format('YYYY-MM-DD'),
        actionType: UserActionType.ALL,
    };
    const [filters, setFilters] = useState<TUserActivityLogListFilters>({
        ...defaultFilters,
        ...filtersFromUrlQuery,
    });

    const [curData, setCurData] = useState<TGetUserActivityLogsResponse>({
        data: [],
        count: 0,
        currentPage: 0,
        lastPage: 0,
    });

    const handlePageChange = (page: number) => {
        setFilters((prev) => ({ ...prev, page }));
    };

    const handleLimitChange = (limit: number) => {
        setFilters((prev) => ({ ...prev, limit }));
    };

    const handleViewLogDetail = (rowData: TUserActivityLog) => {
        history.push(
            `${
                routes[ERoutesType.Private][ERoutesPrivate.UserActivityLog][0]
            }/${rowData.id}`,
        );
    };

    const getAppliedFilters = (filters: TGetUserActivityLogsRequest) => {
        const newFilters = { ...filters };
        Object.keys(newFilters).forEach((key) => {
            if (
                key === 'actionType' &&
                newFilters[key] === UserActionType.ALL
            ) {
                delete newFilters[key];
            }
            !newFilters[key] && delete newFilters[key];
        });
        return queryString.stringify(newFilters);
    };

    const { refetch, isLoading, isFetching } = useUserActivityLogsGet(
        getAppliedFilters(filters),
        {
            onSuccess: (data) => {
                setCurData(data as TGetUserActivityLogsResponse);
            },
        },
    );

    useEffect(() => {
        const queryString = getAppliedFilters(filters);
        history.replace({ pathname: location.pathname, search: queryString });
        refetch();
    }, [filters, history, location.pathname, refetch]);

    return (
        <>
            <Root>
                <UserActivityLogListFilters
                    onFiltersChange={setFilters}
                    currentFilters={filters}
                    defaultFilters={defaultFilters}
                />
                <>
                    <Col justify={'end'}>
                        <TablePagination
                            page={curData.currentPage}
                            totalRows={curData.count}
                            setPage={handlePageChange}
                            setLimit={handleLimitChange}
                            rowsPerPage={filters.limit}
                        />
                    </Col>
                    <MaterialTable<TUserActivityLog>
                        data={curData.data}
                        columns={userActivityLogColumns()}
                        isLoading={isLoading || isFetching}
                        onClickRow={handleViewLogDetail}
                    />
                </>
            </Root>
        </>
    );
};

export default UserActivityLogList;
